import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import MovieCard from '@/components/feature/MovieCard';
import SectionHeading from '@/components/feature/SectionHeading';
import type { Movie } from '@/types/movie';

interface LatestUpdatesProps {
  movies: Movie[];
  pageSize?: number;
}

export default function LatestUpdates({ movies, pageSize = 8 }: LatestUpdatesProps) {
  const [page, setPage] = useState(0);

  const sorted = useMemo(
    () => [...movies].sort((a, b) => (a.updatedAt < b.updatedAt ? 1 : -1)),
    [movies],
  );

  const totalPages = Math.ceil(sorted.length / pageSize);
  const visible = sorted.slice(page * pageSize, page * pageSize + pageSize);

  return (
    <section>
      <SectionHeading
        icon="ri-time-line"
        title="最新影视更新"
        subtitle="按更新时间排序，第一时间掌握新上线内容"
        action={
          <Link
            to="/category/movie"
            className="hidden items-center gap-1 whitespace-nowrap text-sm font-medium text-primary-600 transition-colors hover:text-accent-500 sm:flex"
          >
            查看全部 <i className="ri-arrow-right-s-line"></i>
          </Link>
        }
      />
      <div className="grid grid-cols-2 gap-3 md:gap-4 lg:grid-cols-4">
        {visible.map((movie) => (
          <MovieCard key={movie.id} movie={movie} showType />
        ))}
      </div>
      {totalPages > 1 && (
        <div className="mt-6 flex items-center justify-center gap-2">
          <button
            type="button"
            onClick={() => setPage((prev) => Math.max(0, prev - 1))}
            disabled={page === 0}
            className="flex h-9 items-center gap-1 whitespace-nowrap rounded-md border border-background-200 px-3 text-sm text-foreground-600 transition-colors hover:border-primary-300 hover:text-primary-600 disabled:cursor-not-allowed disabled:opacity-40"
          >
            <i className="ri-arrow-left-s-line"></i> 上一批
          </button>
          {Array.from({ length: totalPages }).map((_, index) => (
            <button
              key={index}
              type="button"
              onClick={() => setPage(index)}
              className={`h-9 w-9 rounded-md text-sm font-medium transition-colors ${
                page === index
                  ? 'bg-primary-600 text-background-50'
                  : 'border border-background-200 text-foreground-600 hover:border-primary-300'
              }`}
            >
              {index + 1}
            </button>
          ))}
          <button
            type="button"
            onClick={() => setPage((prev) => Math.min(totalPages - 1, prev + 1))}
            disabled={page === totalPages - 1}
            className="flex h-9 items-center gap-1 whitespace-nowrap rounded-md border border-background-200 px-3 text-sm text-foreground-600 transition-colors hover:border-primary-300 hover:text-primary-600 disabled:cursor-not-allowed disabled:opacity-40"
          >
            下一批 <i className="ri-arrow-right-s-line"></i>
          </button>
        </div>
      )}
    </section>
  );
}